// Single source of gating truth for every report surface.
//
// Three surfaces decide, independently, whether a user may reach a report:
//
//   1. the tiles on /reports        — ReportsDashboardPage
//   2. the Reports sidebar links    — router.tsx (SIDEBAR_ITEMS → AdminLayout)
//   3. the route guard              — router.tsx (renderReportRoute → ReportGuard)
//
// Each used to carry its own hand-maintained copy of the rules, and the copies
// had already drifted (issue #253): the Demand Forecast *nav link* gated on
// ENABLE_RESTAURANT while its *route* gated on ENABLE_FESTIVE_MODULE, so a
// restaurant tenant with the festive module switched off saw a sidebar link
// that dead-ended on "Report not available". The role levels were duplicated
// the same way, one literal per surface.
//
// All three now read the gates from REPORTS below and evaluate them with
// canAccessReport(), so a link/tile is shown exactly when the route opens.
//
// Statutory-surface caveat: GSTR1 / Tally Export have no GCC equivalent, so
// they are additionally hidden from tiles and nav outside the India regime
// (taxSurface). Their *routes* stay reachable and the pages redirect to
// /reports themselves — a redirect, not a dead end.

import type { PlanFeatureKey } from '@bbpos/types';
import type { ReportVerticalScope } from './ReportGuard';

export type ReportCategory = 'sales' | 'inventory' | 'financial' | 'customer' | 'staff' | 'restaurant';

export type ReportTone = 'brand' | 'indigo' | 'violet' | 'warning' | 'teal' | 'pink' | 'success';

/**
 * Tile presentation on /reports. Reports without it (Overview, Reconciliation)
 * are routed and linked from the sidebar but have no tile.
 */
export interface ReportTileMeta {
  category: ReportCategory;
  description: string;
  tone: ReportTone;
}

export interface ReportDefinition {
  id: string;
  /** Route path — also the key every surface looks the definition up by. */
  path: string;
  /** Tile heading. The sidebar carries its own shorter labels. */
  label: string;
  /** Numeric role level required (ROLE_LEVELS in ReportGuard). */
  minRoleLevel: number;
  /** Business-vertical gate. Platform roles (>= MODERATOR) bypass it. */
  vertical?: ReportVerticalScope;
  featureFlag?: string;
  /**
   * Plan feature (docs/plan-limits-plan.md §3). Missing from the plan, the report is
   * still offered — with a lock — and its route shows LockedFeature. Core reports
   * (Overview, Sales, GST, GSTR-1, Payments, Profit & Loss) have none.
   */
  planFeature?: PlanFeatureKey;
  /** India-only statutory surface — hidden from tiles/nav in other tax regimes. */
  taxSurface?: 'GSTR1' | 'TALLY';
  tile?: ReportTileMeta;
}

export const REPORTS: ReportDefinition[] = [
  { id: 'dashboard',      path: '/reports',                label: 'Reports',            minRoleLevel: 30 },
  { id: 'reconciliation', path: '/reports/reconciliation', label: 'Reconciliation',     minRoleLevel: 30, planFeature: 'online_payments' },

  { id: 'sales',         label: 'Sales Report',        path: '/reports/sales',         minRoleLevel: 30, tile: { category: 'sales',     tone: 'brand',   description: 'Daily, weekly and monthly revenue trends with growth comparison.' } },
  { id: 'payments',      label: 'Payment Breakdown',   path: '/reports/payments',      minRoleLevel: 30, tile: { category: 'sales',     tone: 'indigo',  description: 'Sales split by Cash, UPI, Card, Wallet and Split tenders.' } },
  { id: 'top-products',  label: 'Top Products',        path: '/reports/top-products',  minRoleLevel: 30, planFeature: 'advanced_reports', tile: { category: 'sales',     tone: 'brand',   description: 'Ranked best-sellers by revenue or quantity for any period.' } },
  { id: 'categories',    label: 'Category Sales',      path: '/reports/categories',    minRoleLevel: 30, planFeature: 'advanced_reports', tile: { category: 'sales',     tone: 'violet',  description: 'Revenue and volume split across every product category.' } },
  { id: 'discounts',     label: 'Discount Analysis',   path: '/reports/discounts',     minRoleLevel: 30, planFeature: 'advanced_reports', tile: { category: 'sales',     tone: 'warning', description: 'Line, bill and voucher discounts — identify margin leakage.' } },
  { id: 'peak-hours',    label: 'Peak Hours',          path: '/reports/peak-hours',    minRoleLevel: 30, planFeature: 'advanced_reports', tile: { category: 'sales',     tone: 'indigo',  description: 'Hour-of-day / day-of-week heatmap — optimise staffing and stock.' } },

  { id: 'gst',           label: 'GST Report',          path: '/reports/gst',           minRoleLevel: 30, tile: { category: 'financial', tone: 'teal',    description: 'CGST / SGST / IGST by slab for the filing period.' } },
  { id: 'gstr1',         label: 'GSTR1 Export',        path: '/reports/gstr1',         minRoleLevel: 50, taxSurface: 'GSTR1', tile: { category: 'financial', tone: 'teal',    description: 'GST portal CSVs for B2B, B2CL, B2CS and credit / debit notes.' } },
  { id: 'pnl',           label: 'Profit & Loss',       path: '/reports/pnl',           minRoleLevel: 50, tile: { category: 'financial', tone: 'success', description: 'Revenue, COGS, expenses and net profit for the selected period.' } },
  { id: 'tally-export',  label: 'Tally Export',        path: '/reports/tally-export',  minRoleLevel: 50, taxSurface: 'TALLY', planFeature: 'tally_export', tile: { category: 'financial', tone: 'teal',    description: 'Tally Prime-compatible XML/CSV with full GST mapping.' } },
  // QA PU4-010 (docs/purchase-gaps-plan.md §5.8, G9). No taxSurface: a VAT tenant gets the same reports with one VAT column.
  { id: 'purchase-register', label: 'Purchase Register', path: '/reports/purchase-register', minRoleLevel: 30, planFeature: 'purchase', tile: { category: 'financial', tone: 'indigo',  description: 'Posted supplier bills and debit notes with GSTIN, invoice no., taxable value and tax.' } },
  { id: 'payables-ageing',   label: 'Payables Ageing',   path: '/reports/payables-ageing',   minRoleLevel: 30, planFeature: 'purchase', tile: { category: 'financial', tone: 'warning', description: 'What you owe each supplier: not yet due, 1-30, 31-60, 61-90 and 90+ days late.' } },
  { id: 'input-tax',         label: 'Input Tax Summary', path: '/reports/input-tax',         minRoleLevel: 30, planFeature: 'purchase', tile: { category: 'financial', tone: 'teal',    description: 'Tax paid on purchases by month and rate, less debit notes — your input tax credit.' } },

  { id: 'top-customers', label: 'Top Customers',       path: '/reports/top-customers', minRoleLevel: 30, planFeature: 'advanced_reports', tile: { category: 'customer',  tone: 'violet',  description: 'Highest spenders with visit frequency and loyalty tier.' } },
  { id: 'repeat-visits', label: 'Repeat Visits',       path: '/reports/repeat-visits', minRoleLevel: 30, planFeature: 'advanced_reports', tile: { category: 'customer',  tone: 'pink',    description: 'First-time vs returning customer split and retention trend.' } },

  { id: 'cashiers',         label: 'Cashier Performance',    path: '/reports/cashiers',         minRoleLevel: 30, planFeature: 'advanced_reports', tile: { category: 'staff', tone: 'indigo',  description: 'Sales, bills, average value, returns and voids per cashier.' } },
  { id: 'expense-analysis', label: 'Expense Analysis',       path: '/reports/expense-analysis', minRoleLevel: 50, planFeature: 'advanced_reports', tile: { category: 'staff', tone: 'warning', description: 'Category, staff and approval-delay insights with trend chart.' } },
  { id: 'staff-expense',    label: 'Staff Expense Tracking', path: '/reports/staff-expense',    minRoleLevel: 30, planFeature: 'advanced_reports', tile: { category: 'staff', tone: 'warning', description: 'Per-staff submission, approval and rejection patterns.' } },

  { id: 'table-turnover',      label: 'Table Turnover',      path: '/reports/table-turnover',      minRoleLevel: 30, vertical: 'RESTAURANT', planFeature: 'advanced_reports', tile: { category: 'restaurant', tone: 'violet',  description: 'Seating duration, turnover rate and revenue per table.' } },
  { id: 'kitchen-performance', label: 'Kitchen Performance', path: '/reports/kitchen-performance', minRoleLevel: 30, vertical: 'RESTAURANT', planFeature: 'advanced_reports', tile: { category: 'restaurant', tone: 'pink',    description: 'Prep time by station, items per hour and delay tracking.' } },
  { id: 'cancellation-trends', label: 'Cancellation Trends', path: '/reports/cancellation-trends', minRoleLevel: 30, vertical: 'RESTAURANT', planFeature: 'advanced_reports', tile: { category: 'restaurant', tone: 'warning', description: 'Cancel rate, hourly pattern, top cancelled items and reasons.' } },
  { id: 'demand-forecast',     label: 'Demand Forecast',     path: '/reports/demand-forecast',     minRoleLevel: 30, vertical: 'RESTAURANT', featureFlag: 'ENABLE_FESTIVE_MODULE', planFeature: 'demand_forecast', tile: { category: 'restaurant', tone: 'violet', description: 'Festival & daily demand prediction with auto-PO for shortfalls.' } },
];

const REPORTS_BY_PATH: Record<string, ReportDefinition> = Object.fromEntries(
  REPORTS.map((r) => [r.path, r]),
);

/** The definition for a route path, or undefined if the path is not a report. */
export function getReport(path: string): ReportDefinition | undefined {
  return REPORTS_BY_PATH[path];
}

export interface ReportAccessContext {
  /** Numeric role level (ROLE_LEVELS in ReportGuard). */
  roleLevel: number;
  /** Tenant vertical. Absent for platform users, who have no tenant. */
  businessType?: string;
  flags: Record<string, boolean>;
  tax: { showGstr1: boolean; showTally: boolean };
}

/** Platform roles start at MODERATOR — they drill into any tenant. */
const PLATFORM_ROLE_LEVEL = 60;

/**
 * Whether this user may open this report. ReportGuard applies the same role,
 * vertical and feature-flag rules on the route (it leaves taxSurface to the
 * page's own redirect), so a tile or nav link is offered only when the route
 * will actually render.
 *
 * The plan is deliberately not checked here: a report missing from the plan is
 * still offered, with a lock, and its route shows LockedFeature (plan §7).
 */
export function canAccessReport(report: ReportDefinition, ctx: ReportAccessContext): boolean {
  if (ctx.roleLevel < report.minRoleLevel) return false;

  // Platform roles bypass the vertical gate, matching ReportGuard.
  const isPlatform = ctx.roleLevel >= PLATFORM_ROLE_LEVEL;
  if (
    report.vertical &&
    report.vertical !== 'SHARED' &&
    !isPlatform &&
    ctx.businessType &&
    ctx.businessType !== report.vertical
  ) {
    return false;
  }

  if (report.featureFlag && !ctx.flags[report.featureFlag]) return false;

  if (report.taxSurface === 'GSTR1' && !ctx.tax.showGstr1) return false;
  if (report.taxSurface === 'TALLY' && !ctx.tax.showTally) return false;

  return true;
}

/** Every report this user may open, in registry order. */
export function visibleReports(ctx: ReportAccessContext): ReportDefinition[] {
  return REPORTS.filter((r) => canAccessReport(r, ctx));
}
